import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../components/ui/chart';
import { cn } from '../../lib/utils';
import type { AmBrandChartData } from '../../lib/accountAnalytics';
import type { AnalyticsBreakdown } from '../../types/accountAnalytics';

// The categorical palette — the five on-brand chart CSS vars (index.css), cycled
// for pie slices. Bars use a single Kyck-green series.
const PALETTE = [
  'var(--color-chart-1)',
  'var(--color-chart-2)',
  'var(--color-chart-3)',
  'var(--color-chart-4)',
  'var(--color-chart-5)',
];

function ChartCard({
  title,
  children,
  empty,
  action,
}: {
  title: string;
  children: React.ReactNode;
  empty: boolean;
  // Optional right-aligned header control (e.g. a Grouped/Stacked toggle).
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-slate-700">{title}</h3>
        {action}
      </div>
      {empty ? (
        <p className="flex h-56 items-center justify-center text-sm text-slate-400">No data</p>
      ) : (
        children
      )}
    </div>
  );
}

// A horizontal bar chart of a breakdown (accounts per AM / per brand). Capped to
// the top `limit` buckets so a long tail doesn't crush the layout.
export function BreakdownBarChart({
  title,
  data,
  limit = 12,
}: {
  title: string;
  data: AnalyticsBreakdown[];
  limit?: number;
}) {
  const rows = data.slice(0, limit);
  const config: ChartConfig = { count: { label: 'Accounts', color: 'var(--color-chart-1)' } };
  return (
    <ChartCard title={title} empty={rows.length === 0}>
      <ChartContainer config={config} className="aspect-auto h-56 w-full">
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ left: 8, right: 24, top: 4, bottom: 4 }}
          accessibilityLayer
        >
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="label"
            tickLine={false}
            axisLine={false}
            width={128}
            tick={{ fontSize: 11 }}
          />
          <ChartTooltip cursor={{ fill: 'var(--color-muted)', opacity: 0.4 }} content={<ChartTooltipContent />} />
          <Bar dataKey="count" fill="var(--color-count)" radius={[0, 3, 3, 0]} isAnimationActive={false}>
            <LabelList dataKey="count" position="right" className="fill-slate-500 text-[11px]" />
          </Bar>
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}

// A donut of a breakdown (type / industry mix). Slices cycle the categorical
// palette; a legend maps colour → label.
export function BreakdownPieChart({
  title,
  data,
}: {
  title: string;
  data: AnalyticsBreakdown[];
}) {
  const config: ChartConfig = Object.fromEntries(
    data.map((d, i) => [d.key, { label: d.label, color: PALETTE[i % PALETTE.length] }]),
  );
  return (
    <ChartCard title={title} empty={data.length === 0}>
      <ChartContainer config={config} className="mx-auto aspect-square h-56">
        <PieChart accessibilityLayer>
          <ChartTooltip content={<ChartTooltipContent nameKey="label" hideLabel />} />
          <Pie
            data={data}
            dataKey="count"
            nameKey="label"
            innerRadius={45}
            outerRadius={80}
            strokeWidth={2}
            isAnimationActive={false}
          >
            {data.map((d, i) => (
              <Cell key={d.key} fill={PALETTE[i % PALETTE.length]} />
            ))}
          </Pie>
          <ChartLegend content={<ChartLegendContent nameKey="label" />} className="flex-wrap" />
        </PieChart>
      </ChartContainer>
    </ChartCard>
  );
}

// The AM lens's wide "accounts per AM per brand" chart: one cluster per AM along
// the x-axis, one <Bar> per brand within it (grouped by default; a Stacked
// toggle compacts it into one segmented column per AM). Full-width with
// horizontal scroll when there are many AMs. Brand colours cycle the palette;
// the legend + tooltip map colour → brand. Bar fills are set directly (not via
// ChartContainer's --color-<key> vars, which can't hold brand names with spaces).
export function AmByBrandChart({ title, data }: { title: string; data: AmBrandChartData }) {
  const [stacked, setStacked] = useState(false);
  // config supplies legend/tooltip LABELS only (no color → no invalid CSS var);
  // the swatch colours come from each Bar's fill via recharts' payload.
  const config: ChartConfig = Object.fromEntries(data.brands.map((b) => [b, { label: b }]));

  // Give each AM cluster enough room for its grouped bars; scroll if wider than
  // the card. Stacked needs one slim column per AM, grouped needs one per brand.
  const perAm = stacked ? 64 : Math.max(64, data.brands.length * 22 + 24);
  const minWidth = Math.max(480, data.data.length * perAm + 80);

  const toggle = (
    <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs">
      {(['grouped', 'stacked'] as const).map((mode) => {
        const active = (mode === 'stacked') === stacked;
        return (
          <button
            key={mode}
            type="button"
            onClick={() => setStacked(mode === 'stacked')}
            className={cn(
              'px-2 py-1 font-medium capitalize transition-colors',
              active ? 'bg-kyck-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
            )}
          >
            {mode}
          </button>
        );
      })}
    </div>
  );

  return (
    <ChartCard title={title} empty={data.data.length === 0} action={toggle}>
      <div className="overflow-x-auto">
        <div style={{ minWidth }}>
          <ChartContainer config={config} className="aspect-auto h-72 w-full">
            <BarChart data={data.data} margin={{ left: 4, right: 8, top: 8, bottom: 0 }} accessibilityLayer>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="amLabel"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval={0}
                tick={{ fontSize: 11 }}
              />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={28} tick={{ fontSize: 11 }} />
              <ChartTooltip cursor={{ fill: 'var(--color-muted)', opacity: 0.4 }} content={<ChartTooltipContent />} />
              {data.brands.map((brand, i) => (
                <Bar
                  key={brand}
                  dataKey={brand}
                  name={brand}
                  fill={PALETTE[i % PALETTE.length]}
                  stackId={stacked ? 'am' : undefined}
                  radius={stacked ? 0 : [2, 2, 0, 0]}
                  maxBarSize={48}
                  isAnimationActive={false}
                />
              ))}
              <ChartLegend content={<ChartLegendContent />} className="flex-wrap" />
            </BarChart>
          </ChartContainer>
        </div>
      </div>
    </ChartCard>
  );
}
